import { useCallback } from "react";

import { OrganizationCategory, TicketingFormCategory } from "@simplyk/common";

import { useCurrentUserContext } from "../contexts/CurrentUserContext";

import { Translate } from "@/common/hooks/useTranslate";

type SupportedCategory =
  | OrganizationCategory.AnimalWelfare
  | OrganizationCategory.CommunityService
  | OrganizationCategory.Culture
  | OrganizationCategory.Education
  | OrganizationCategory.Environment
  | OrganizationCategory.Health
  | OrganizationCategory.Humanitarian
  | OrganizationCategory.Other
  | OrganizationCategory.Politic
  | OrganizationCategory.Religion
  | OrganizationCategory.SocialService
  | OrganizationCategory.SportsAndLeisure
  | OrganizationCategory.StudentGroup
  | OrganizationCategory.Veterans;

const SUPPORTED_CATEGORIES: SupportedCategory[] = [
  OrganizationCategory.AnimalWelfare,
  OrganizationCategory.CommunityService,
  OrganizationCategory.Culture,
  OrganizationCategory.Education,
  OrganizationCategory.Environment,
  OrganizationCategory.Health,
  OrganizationCategory.Humanitarian,
  OrganizationCategory.Other,
  OrganizationCategory.Politic,
  OrganizationCategory.Religion,
  OrganizationCategory.SocialService,
  OrganizationCategory.SportsAndLeisure,
  OrganizationCategory.StudentGroup,
  OrganizationCategory.Veterans,
];

const isSupportedCategory = (category: OrganizationCategory): category is SupportedCategory =>
  (SUPPORTED_CATEGORIES as OrganizationCategory[]).includes(category);

const crossSellTitleKeys = {
  [OrganizationCategory.AnimalWelfare]: {
    [TicketingFormCategory.Event]: "crossSell.AnimalWelfare.Event.title",
    [TicketingFormCategory.Lottery]: "crossSell.AnimalWelfare.Lottery.title",
    [TicketingFormCategory.Shop]: "crossSell.AnimalWelfare.Shop.title",
    [TicketingFormCategory.MembershipV2]: "crossSell.AnimalWelfare.MembershipV2.title",
    [TicketingFormCategory.Auction]: "crossSell.AnimalWelfare.Auction.title",
    [TicketingFormCategory.Custom]: "crossSell.AnimalWelfare.Event.title",
  },
  [OrganizationCategory.CommunityService]: {
    [TicketingFormCategory.Event]: "crossSell.CommunityService.Event.title",
    [TicketingFormCategory.Lottery]: "crossSell.CommunityService.Lottery.title",
    [TicketingFormCategory.Shop]: "crossSell.CommunityService.Shop.title",
    [TicketingFormCategory.MembershipV2]: "crossSell.CommunityService.MembershipV2.title",
    [TicketingFormCategory.Auction]: "crossSell.CommunityService.Auction.title",
    [TicketingFormCategory.Custom]: "crossSell.CommunityService.Event.title",
  },
  [OrganizationCategory.Culture]: {
    [TicketingFormCategory.Event]: "crossSell.Culture.Event.title",
    [TicketingFormCategory.Lottery]: "crossSell.Culture.Lottery.title",
    [TicketingFormCategory.Shop]: "crossSell.Culture.Shop.title",
    [TicketingFormCategory.MembershipV2]: "crossSell.Culture.MembershipV2.title",
    [TicketingFormCategory.Auction]: "crossSell.Culture.Auction.title",
    [TicketingFormCategory.Custom]: "crossSell.Culture.Event.title",
  },
  [OrganizationCategory.Education]: {
    [TicketingFormCategory.Event]: "crossSell.Education.Event.title",
    [TicketingFormCategory.Lottery]: "crossSell.Education.Lottery.title",
    [TicketingFormCategory.Shop]: "crossSell.Education.Shop.title",
    [TicketingFormCategory.MembershipV2]: "crossSell.Education.MembershipV2.title",
    [TicketingFormCategory.Auction]: "crossSell.Education.Auction.title",
    [TicketingFormCategory.Custom]: "crossSell.Education.Event.title",
  },
  [OrganizationCategory.Environment]: {
    [TicketingFormCategory.Event]: "crossSell.Environment.Event.title",
    [TicketingFormCategory.Lottery]: "crossSell.Environment.Lottery.title",
    [TicketingFormCategory.Shop]: "crossSell.Environment.Shop.title",
    [TicketingFormCategory.MembershipV2]: "crossSell.Environment.MembershipV2.title",
    [TicketingFormCategory.Auction]: "crossSell.Environment.Auction.title",
    [TicketingFormCategory.Custom]: "crossSell.Environment.Event.title",
  },
  [OrganizationCategory.Health]: {
    [TicketingFormCategory.Event]: "crossSell.Health.Event.title",
    [TicketingFormCategory.Lottery]: "crossSell.Health.Lottery.title",
    [TicketingFormCategory.Shop]: "crossSell.Health.Shop.title",
    [TicketingFormCategory.MembershipV2]: "crossSell.Health.MembershipV2.title",
    [TicketingFormCategory.Auction]: "crossSell.Health.Auction.title",
    [TicketingFormCategory.Custom]: "crossSell.Health.Event.title",
  },
  [OrganizationCategory.Humanitarian]: {
    [TicketingFormCategory.Event]: "crossSell.Humanitarian.Event.title",
    [TicketingFormCategory.Lottery]: "crossSell.Humanitarian.Lottery.title",
    [TicketingFormCategory.Shop]: "crossSell.Humanitarian.Shop.title",
    [TicketingFormCategory.MembershipV2]: "crossSell.Humanitarian.MembershipV2.title",
    [TicketingFormCategory.Auction]: "crossSell.Humanitarian.Auction.title",
    [TicketingFormCategory.Custom]: "crossSell.Humanitarian.Event.title",
  },
  [OrganizationCategory.Other]: {
    [TicketingFormCategory.Event]: "crossSell.Other.Event.title",
    [TicketingFormCategory.Lottery]: "crossSell.Other.Lottery.title",
    [TicketingFormCategory.Shop]: "crossSell.Other.Shop.title",
    [TicketingFormCategory.MembershipV2]: "crossSell.Other.MembershipV2.title",
    [TicketingFormCategory.Auction]: "crossSell.Other.Auction.title",
    [TicketingFormCategory.Custom]: "crossSell.Other.Event.title",
  },
  [OrganizationCategory.Politic]: {
    [TicketingFormCategory.Event]: "crossSell.Politic.Event.title",
    [TicketingFormCategory.Lottery]: "crossSell.Politic.Lottery.title",
    [TicketingFormCategory.Shop]: "crossSell.Politic.Shop.title",
    [TicketingFormCategory.MembershipV2]: "crossSell.Politic.MembershipV2.title",
    [TicketingFormCategory.Auction]: "crossSell.Politic.Auction.title",
    [TicketingFormCategory.Custom]: "crossSell.Politic.Event.title",
  },
  [OrganizationCategory.Religion]: {
    [TicketingFormCategory.Event]: "crossSell.Religion.Event.title",
    [TicketingFormCategory.Lottery]: "crossSell.Religion.Lottery.title",
    [TicketingFormCategory.Shop]: "crossSell.Religion.Shop.title",
    [TicketingFormCategory.MembershipV2]: "crossSell.Religion.MembershipV2.title",
    [TicketingFormCategory.Auction]: "crossSell.Religion.Auction.title",
    [TicketingFormCategory.Custom]: "crossSell.Religion.Event.title",
  },
  [OrganizationCategory.SocialService]: {
    [TicketingFormCategory.Event]: "crossSell.SocialService.Event.title",
    [TicketingFormCategory.Lottery]: "crossSell.SocialService.Lottery.title",
    [TicketingFormCategory.Shop]: "crossSell.SocialService.Shop.title",
    [TicketingFormCategory.MembershipV2]: "crossSell.SocialService.MembershipV2.title",
    [TicketingFormCategory.Auction]: "crossSell.SocialService.Auction.title",
    [TicketingFormCategory.Custom]: "crossSell.SocialService.Event.title",
  },
  [OrganizationCategory.SportsAndLeisure]: {
    [TicketingFormCategory.Event]: "crossSell.SportsAndLeisure.Event.title",
    [TicketingFormCategory.Lottery]: "crossSell.SportsAndLeisure.Lottery.title",
    [TicketingFormCategory.Shop]: "crossSell.SportsAndLeisure.Shop.title",
    [TicketingFormCategory.MembershipV2]: "crossSell.SportsAndLeisure.MembershipV2.title",
    [TicketingFormCategory.Auction]: "crossSell.SportsAndLeisure.Auction.title",
    [TicketingFormCategory.Custom]: "crossSell.SportsAndLeisure.Event.title",
  },
  [OrganizationCategory.StudentGroup]: {
    [TicketingFormCategory.Event]: "crossSell.StudentGroup.Event.title",
    [TicketingFormCategory.Lottery]: "crossSell.StudentGroup.Lottery.title",
    [TicketingFormCategory.Shop]: "crossSell.StudentGroup.Shop.title",
    [TicketingFormCategory.MembershipV2]: "crossSell.StudentGroup.MembershipV2.title",
    [TicketingFormCategory.Auction]: "crossSell.StudentGroup.Auction.title",
    [TicketingFormCategory.Custom]: "crossSell.StudentGroup.Event.title",
  },
  [OrganizationCategory.Veterans]: {
    [TicketingFormCategory.Event]: "crossSell.Veterans.Event.title",
    [TicketingFormCategory.Lottery]: "crossSell.Veterans.Lottery.title",
    [TicketingFormCategory.Shop]: "crossSell.Veterans.Shop.title",
    [TicketingFormCategory.MembershipV2]: "crossSell.Veterans.MembershipV2.title",
    [TicketingFormCategory.Auction]: "crossSell.Veterans.Auction.title",
    [TicketingFormCategory.Custom]: "crossSell.Veterans.Event.title",
  },
} as const;

const crossSellDescriptionKeys = {
  [OrganizationCategory.AnimalWelfare]: {
    [TicketingFormCategory.Event]: "crossSell.AnimalWelfare.Event.description",
    [TicketingFormCategory.Lottery]: "crossSell.AnimalWelfare.Lottery.description",
    [TicketingFormCategory.Shop]: "crossSell.AnimalWelfare.Shop.description",
    [TicketingFormCategory.MembershipV2]: "crossSell.AnimalWelfare.MembershipV2.description",
    [TicketingFormCategory.Auction]: "crossSell.AnimalWelfare.Auction.description",
    [TicketingFormCategory.Custom]: "crossSell.AnimalWelfare.Event.description",
  },
  [OrganizationCategory.CommunityService]: {
    [TicketingFormCategory.Event]: "crossSell.CommunityService.Event.description",
    [TicketingFormCategory.Lottery]: "crossSell.CommunityService.Lottery.description",
    [TicketingFormCategory.Shop]: "crossSell.CommunityService.Shop.description",
    [TicketingFormCategory.MembershipV2]: "crossSell.CommunityService.MembershipV2.description",
    [TicketingFormCategory.Auction]: "crossSell.CommunityService.Auction.description",
    [TicketingFormCategory.Custom]: "crossSell.CommunityService.Event.description",
  },
  [OrganizationCategory.Culture]: {
    [TicketingFormCategory.Event]: "crossSell.Culture.Event.description",
    [TicketingFormCategory.Lottery]: "crossSell.Culture.Lottery.description",
    [TicketingFormCategory.Shop]: "crossSell.Culture.Shop.description",
    [TicketingFormCategory.MembershipV2]: "crossSell.Culture.MembershipV2.description",
    [TicketingFormCategory.Auction]: "crossSell.Culture.Auction.description",
    [TicketingFormCategory.Custom]: "crossSell.Culture.Event.description",
  },
  [OrganizationCategory.Education]: {
    [TicketingFormCategory.Event]: "crossSell.Education.Event.description",
    [TicketingFormCategory.Lottery]: "crossSell.Education.Lottery.description",
    [TicketingFormCategory.Shop]: "crossSell.Education.Shop.description",
    [TicketingFormCategory.MembershipV2]: "crossSell.Education.MembershipV2.description",
    [TicketingFormCategory.Auction]: "crossSell.Education.Auction.description",
    [TicketingFormCategory.Custom]: "crossSell.Education.Event.description",
  },
  [OrganizationCategory.Environment]: {
    [TicketingFormCategory.Event]: "crossSell.Environment.Event.description",
    [TicketingFormCategory.Lottery]: "crossSell.Environment.Lottery.description",
    [TicketingFormCategory.Shop]: "crossSell.Environment.Shop.description",
    [TicketingFormCategory.MembershipV2]: "crossSell.Environment.MembershipV2.description",
    [TicketingFormCategory.Auction]: "crossSell.Environment.Auction.description",
    [TicketingFormCategory.Custom]: "crossSell.Environment.Event.description",
  },
  [OrganizationCategory.Health]: {
    [TicketingFormCategory.Event]: "crossSell.Health.Event.description",
    [TicketingFormCategory.Lottery]: "crossSell.Health.Lottery.description",
    [TicketingFormCategory.Shop]: "crossSell.Health.Shop.description",
    [TicketingFormCategory.MembershipV2]: "crossSell.Health.MembershipV2.description",
    [TicketingFormCategory.Auction]: "crossSell.Health.Auction.description",
    [TicketingFormCategory.Custom]: "crossSell.Health.Event.description",
  },
  [OrganizationCategory.Humanitarian]: {
    [TicketingFormCategory.Event]: "crossSell.Humanitarian.Event.description",
    [TicketingFormCategory.Lottery]: "crossSell.Humanitarian.Lottery.description",
    [TicketingFormCategory.Shop]: "crossSell.Humanitarian.Shop.description",
    [TicketingFormCategory.MembershipV2]: "crossSell.Humanitarian.MembershipV2.description",
    [TicketingFormCategory.Auction]: "crossSell.Humanitarian.Auction.description",
    [TicketingFormCategory.Custom]: "crossSell.Humanitarian.Event.description",
  },
  [OrganizationCategory.Other]: {
    [TicketingFormCategory.Event]: "crossSell.Other.Event.description",
    [TicketingFormCategory.Lottery]: "crossSell.Other.Lottery.description",
    [TicketingFormCategory.Shop]: "crossSell.Other.Shop.description",
    [TicketingFormCategory.MembershipV2]: "crossSell.Other.MembershipV2.description",
    [TicketingFormCategory.Auction]: "crossSell.Other.Auction.description",
    [TicketingFormCategory.Custom]: "crossSell.Other.Event.description",
  },
  [OrganizationCategory.Politic]: {
    [TicketingFormCategory.Event]: "crossSell.Politic.Event.description",
    [TicketingFormCategory.Lottery]: "crossSell.Politic.Lottery.description",
    [TicketingFormCategory.Shop]: "crossSell.Politic.Shop.description",
    [TicketingFormCategory.MembershipV2]: "crossSell.Politic.MembershipV2.description",
    [TicketingFormCategory.Auction]: "crossSell.Politic.Auction.description",
    [TicketingFormCategory.Custom]: "crossSell.Politic.Event.description",
  },
  [OrganizationCategory.Religion]: {
    [TicketingFormCategory.Event]: "crossSell.Religion.Event.description",
    [TicketingFormCategory.Lottery]: "crossSell.Religion.Lottery.description",
    [TicketingFormCategory.Shop]: "crossSell.Religion.Shop.description",
    [TicketingFormCategory.MembershipV2]: "crossSell.Religion.MembershipV2.description",
    [TicketingFormCategory.Auction]: "crossSell.Religion.Auction.description",
    [TicketingFormCategory.Custom]: "crossSell.Religion.Event.description",
  },
  [OrganizationCategory.SocialService]: {
    [TicketingFormCategory.Event]: "crossSell.SocialService.Event.description",
    [TicketingFormCategory.Lottery]: "crossSell.SocialService.Lottery.description",
    [TicketingFormCategory.Shop]: "crossSell.SocialService.Shop.description",
    [TicketingFormCategory.MembershipV2]: "crossSell.SocialService.MembershipV2.description",
    [TicketingFormCategory.Auction]: "crossSell.SocialService.Auction.description",
    [TicketingFormCategory.Custom]: "crossSell.SocialService.Event.description",
  },
  [OrganizationCategory.SportsAndLeisure]: {
    [TicketingFormCategory.Event]: "crossSell.SportsAndLeisure.Event.description",
    [TicketingFormCategory.Lottery]: "crossSell.SportsAndLeisure.Lottery.description",
    [TicketingFormCategory.Shop]: "crossSell.SportsAndLeisure.Shop.description",
    [TicketingFormCategory.MembershipV2]: "crossSell.SportsAndLeisure.MembershipV2.description",
    [TicketingFormCategory.Auction]: "crossSell.SportsAndLeisure.Auction.description",
    [TicketingFormCategory.Custom]: "crossSell.SportsAndLeisure.Event.description",
  },
  [OrganizationCategory.StudentGroup]: {
    [TicketingFormCategory.Event]: "crossSell.StudentGroup.Event.description",
    [TicketingFormCategory.Lottery]: "crossSell.StudentGroup.Lottery.description",
    [TicketingFormCategory.Shop]: "crossSell.StudentGroup.Shop.description",
    [TicketingFormCategory.MembershipV2]: "crossSell.StudentGroup.MembershipV2.description",
    [TicketingFormCategory.Auction]: "crossSell.StudentGroup.Auction.description",
    [TicketingFormCategory.Custom]: "crossSell.StudentGroup.Event.description",
  },
  [OrganizationCategory.Veterans]: {
    [TicketingFormCategory.Event]: "crossSell.Veterans.Event.description",
    [TicketingFormCategory.Lottery]: "crossSell.Veterans.Lottery.description",
    [TicketingFormCategory.Shop]: "crossSell.Veterans.Shop.description",
    [TicketingFormCategory.MembershipV2]: "crossSell.Veterans.MembershipV2.description",
    [TicketingFormCategory.Auction]: "crossSell.Veterans.Auction.description",
    [TicketingFormCategory.Custom]: "crossSell.Veterans.Event.description",
  },
} as const;

export const useGetCrossSellTicketingInput = () => {
  const { organization } = useCurrentUserContext();
  const category = organization?.category;

  return useCallback(
    (formCategory: TicketingFormCategory, t: Translate) => {
      if (!category || !isSupportedCategory(category) || formCategory === TicketingFormCategory.Sweepstakes) {
        return {
          title: undefined,
          description: undefined,
        };
      }

      return {
        title: t("dashboard", crossSellTitleKeys[category][formCategory]),
        description: t("dashboard", crossSellDescriptionKeys[category][formCategory]),
      };
    },
    [category]
  );
};
